<script lang="ts" setup>
  import { useId, useTemplateRef, computed, mergeProps } from 'vue';
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';

  interface Props {
    id?: string | undefined;
    name?: string | undefined;
    label?: string | undefined;
    placeholder?: string | undefined;
    required?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    loading?: boolean;
    small?: boolean;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<Props>(), {
    required: false,
    readonly: false,
    disabled: false,
  });

  const context = useFormContext();
  const random = useId();
  const input = useTemplateRef('input');

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value),
    set: (value) => setModelWithContext(props.name, context, value),
  });

  const id = computed(() => {
    return props.id ?? random;
  });

  const readonly = computed(() => {
    return Boolean(props.readonly || (context && context.readonly.value));
  });

  function onFocus() {
    if (input.value) {
      input.value.select();
    }
  }

  const classes = computed(() => {
    return {
      'control--number--small': props.small,
    };
  });

  function increase() {
    if (input.value && !props.disabled && !readonly.value) {
      let previous = input.value.valueAsNumber;

      input.value.stepUp();

      // Prevent unnecessary input/change events.
      if (input.value.valueAsNumber === previous) {
        return;
      }

      input.value.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
      input.value.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    }
  }

  function decrease() {
    if (input.value && !props.disabled && !readonly.value) {
      let previous = input.value.valueAsNumber;

      input.value.stepDown();

      // Prevent unnecessary input/change events.
      if (input.value.valueAsNumber === previous) {
        return;
      }

      input.value.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
      input.value.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    }
  }
</script>
<template>
  <SfControlWrapper v-slot="{errorId}" :class="classes" class="control--number" v-bind="{ id, name, label, required }">
    <SfButton :aria-label="$t('SHOP.COMMON.ACTION.SUBTRACT')" :loading :small class="btn-minus" rounded square transparent @click="decrease">
      <SfIconMinus aria-hidden="true" />
    </SfButton>
    <input ref="input" v-model="model" :aria-describedby="errorId" :disabled="loading" class="control number" type="number" v-bind="mergeProps({ id, name, readonly, required, onFocus }, $attrs)">
    <SfButton :aria-label="$t('SHOP.COMMON.ACTION.ADD')" :loading :small class="btn-plus" rounded square transparent @click="increase">
      <SfIconPlus aria-hidden="true" />
    </SfButton>
  </SfControlWrapper>
</template>
<style lang="scss" scoped>
  .control--number {
    :deep(.label) {
      padding-left: var(--sf-input-border-radius);
    }

    :deep(.control-container) {
      .btn-plus,
      .btn-minus {
        position: absolute;
        top: 50%;
        height: calc(var(--sf-input-number-btn-size) - (var(--sf-input-border-top-width) + var(--sf-input-border-bottom-width)));
        width: var(--sf-input-number-btn-size);
        font-size: var(--sf-input-number-icon-size);
        transform: translateY(-50%);
        aspect-ratio: 1;
        vertical-align: middle;
      }

      .btn-plus {
        right: var(--sf-input-border-right-width);
      }

      .btn-minus {
        left: var(--sf-input-border-left-width);
      }
    }

    &--small {
      --sf-input-number-btn-size: var(--sf-input-number-btn-size-sm);
      --sf-input-number-min-width: var(--sf-input-number-min-width-sm)
    }
  }

  .number {
    min-width: var(--sf-input-number-min-width);
    height: var(--sf-input-number-btn-size);
    text-align: center;
    font-weight: var(--sf-input-number-font-weight, var(--font-weight-semi));
    appearance: textfield;

    --sf-input-padding-right: var(--sf-input-number-btn-size, --sf-input-padding-right);
    --sf-input-padding-left: var(--sf-input-number-btn-size, --sf-input-padding-left);

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none;
    }
  }
</style>

